import { useCallback } from 'react';
import { FOCUSABLE_SELECTORS } from './navigation.constants';

/**
 * Shared hook for focusing items in navigation components
 * @param {Object} params
 * @param {React.RefObject} params.containerRef - Reference to the container element
 * @param {number} params.itemCount - Total number of items
 * @param {Array} params.items - Array of React elements (from Children.toArray)
 * @param {Function} params.setFocusIndex - State setter for focus index
 * @param {number} params.focusIndex - Current focus index state (fallback for getCurrentFocusIndex)
 * @param {Function} params.onItemFocus - Optional callback when item receives focus
 * @returns {{ focusItem: Function, getCurrentFocusIndex: Function }}
 */
export function useFocusItem({
  containerRef,
  itemCount,
  items,
  setFocusIndex,
  focusIndex,
  onItemFocus,
}) {
  // Find which item index contains the currently focused element
  const getCurrentFocusIndex = useCallback(() => {
    if (!containerRef.current) return focusIndex;

    const activeElement = document.activeElement;
    if (!activeElement || !containerRef.current.contains(activeElement)) {
      return focusIndex;
    }

    const children = Array.from(containerRef.current.children);
    const index = children.findIndex(
      child => child.contains(activeElement) || child === activeElement,
    );

    return index !== -1 ? index : focusIndex;
  }, [containerRef, focusIndex]);

  const focusItem = useCallback(
    index => {
      if (index < 0 || index >= itemCount) return;

      setFocusIndex(index);

      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (!containerRef.current) return;

        // Find items by position in the container's children
        const children = Array.from(containerRef.current.children);
        if (!children[index]) return;

        // First try to focus the item itself if it has a tabindex
        if (children[index].tabIndex >= 0) {
          // If the item itself has a tabindex, focus it
          children[index].focus();
          if (onItemFocus) {
            onItemFocus(items[index], index);
          }
        } else {
          // Otherwise, focus the first focusable element within the item
          const selector = FOCUSABLE_SELECTORS.join(', ');
          const firstFocusable = children[index].querySelector(selector);
          firstFocusable?.focus();
          if (onItemFocus) {
            onItemFocus(items[index], index);
          }
        }
      });
    },
    [containerRef, itemCount, items, setFocusIndex, onItemFocus],
  );

  return { focusItem, getCurrentFocusIndex };
}
